import { Spinner, type SpinnerProps } from '@tempmee/react';
import { type CSSProperties } from 'react';

interface ICenteredSpinnerProps extends SpinnerProps {
  /**
   * Minimum height for the centering container. Pass this when the spinner lives in an
   * auto-height parent (e.g. a tab panel) where `height: 100%` collapses to the spinner —
   * it gives the flex container a concrete area to vertically center within.
   */
  minHeight?: CSSProperties['minHeight'];
}

/**
 * DS `Spinner` wrapped in a flex container that fills its parent (width/height 100%)
 * and centers the spinner both axes.
 *
 * Restores the full-area centering behavior of the legacy `CustomSpinner` for
 * full-render / full-panel loading states (where the spinner is returned as the
 * whole component or fills a sized container). The DS `Spinner`'s own `centered`
 * prop only applies `flex-1` and does not fill height, so it top-aligns in
 * non-flex / auto-height parents.
 *
 * For small inline spinners, use `<Spinner>` from `@tempmee/react` directly.
 */
export const CenteredSpinner = ({
  variant = 'default',
  size = 'md',
  minHeight,
  ...rest
}: ICenteredSpinnerProps) => (
  <div
    style={{
      display: 'flex',
      flex: 1,
      width: '100%',
      height: '100%',
      minHeight,
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Spinner variant={variant} size={size} {...rest} />
  </div>
);
